import { useMemo, type RefObject } from "react";

/* ============================================
   Girih — an 8-fold Islamic star lattice that draws itself in from a focal
   point. Pair it with useLamp to light it where the pointer is.
   ============================================ */

const TILE = 120;
const COLS = 11;
const ROWS = 9;
const VIEW_W = TILE * COLS;
const VIEW_H = TILE * ROWS;

/** One tile: an {8/3} star touching its neighbours, plus the small square that joins four stars at a corner. */
function tilePath(cx: number, cy: number): string {
  const r = TILE / 2;
  const pts = Array.from({ length: 8 }, (_, k) => {
    const a = (k * Math.PI) / 4;
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  });
  let star = "";
  for (let i = 0, k = 0; i <= 8; i++, k = (k + 3) % 8) {
    star += `${i ? "L" : "M"}${pts[k][0].toFixed(1)} ${pts[k][1].toFixed(1)}`;
  }
  const o = r * Math.SQRT1_2;
  const [qx, qy] = [cx + r, cy + r];
  const d = r - o;
  const corner = `M${(qx - d).toFixed(1)} ${(qy - d).toFixed(1)}h${(2 * d).toFixed(1)}v${(2 * d).toFixed(1)}h${(-2 * d).toFixed(1)}Z`;
  // A rosette circle in every other tile keeps the field from reading as wallpaper.
  const ring = (Math.round(cx / TILE) + Math.round(cy / TILE)) % 2 === 0
    ? `M${cx - r * 0.3} ${cy}a${r * 0.3} ${r * 0.3} 0 1 0 ${r * 0.6} 0a${r * 0.3} ${r * 0.3} 0 1 0 ${-r * 0.6} 0`
    : "";
  return `${star}Z${corner}${ring}`;
}

interface Tile {
  d: string;
  delay: number;
}

function useTiles(focal: [number, number]): Tile[] {
  return useMemo(() => {
    const out: { d: string; dist: number }[] = [];
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        const cx = col * TILE + TILE / 2;
        const cy = row * TILE + TILE / 2;
        out.push({ d: tilePath(cx, cy), dist: Math.hypot(cx - focal[0], cy - focal[1]) });
      }
    }
    const max = Math.max(...out.map((t) => t.dist));
    return out.map((t) => ({ d: t.d, delay: 0.15 + (t.dist / max) * 1.4 }));
  }, [focal]);
}

function Lattice({ tiles, className }: { tiles: Tile[]; className: string }) {
  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="xMidYMid slice"
      className={`girih-svg ${className}`}
      aria-hidden="true"
      focusable="false"
    >
      {tiles.map((t, i) => (
        <path key={i} d={t.d} pathLength={1} style={{ animationDelay: `${t.delay}s` }} />
      ))}
    </svg>
  );
}

/**
 * The lattice field. Positioned by the parent; `lampRef` receives the lamp variables.
 * `focal` is the point, in lattice units, the drawing radiates from.
 */
export default function Girih({
  lampRef,
  focal = [VIEW_W * 0.78, VIEW_H * 0.82],
  className = "",
}: {
  lampRef?: RefObject<HTMLDivElement>;
  focal?: [number, number];
  className?: string;
}) {
  const tiles = useTiles(focal);
  return (
    <div ref={lampRef} className={`girih ${className}`} aria-hidden="true">
      <Lattice tiles={tiles} className="girih-base" />
      <Lattice tiles={tiles} className="girih-lit" />
    </div>
  );
}
